import { Dispatch, SetStateAction } from 'react'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { Select, InputNumber, Row, Radio } from 'antd'
import style from '@ProdWb/components/ExceptionFailureModal/styles.css'
import { MILESTONE_LIST as milestone } from '@ProdWb/JobList/constants'
import { useSelector } from 'react-redux'
import { isUserQCManager } from '@OpsWb/logic'

import {
    AssignedEntityType,
    ExceptionFailureQuery,
    useCarActiveAssigneesQuery,
    ExceptionFailureType,
} from '~/gql/graphql'
import Text from '~/core/Text'
import Box from '~/core/Box'
import {
    CUSTOMER_IMPACTED_OPTIONS,
    FORM_RULES,
    EXCEPTION_FORM_FIELDS,
} from '../constants'
import { NoteAttachmentType } from '../types'

import ExceptionFileUpload from './ExceptionFileUpload'

const { Option } = Select

type ExceptionFailureDetailsPropsType = {
    form: WrappedFormUtils
    exception?: ExceptionFailureQuery['exceptionFailure']
    confirmFileUpload: (param: string) => void
    exceptionId?: string
    setExceptionAttachment: Dispatch<SetStateAction<NoteAttachmentType[]>>
    exceptionAttachment: NoteAttachmentType[]
}

const ExceptionFailureDetailsSection = (
    props: ExceptionFailureDetailsPropsType,
) => {
    const {
        form,
        form: { getFieldDecorator },
        exception,
        confirmFileUpload,
        exceptionAttachment,
        setExceptionAttachment,
    } = props
    const {
        TYPE,
        MILESTONE,
        ASSIGNED_ENTITY,
        DAYS_LATE,
        CUSTOMER_AFFECTED,
        FICTIV_ASSIGNEE,
    } = EXCEPTION_FORM_FIELDS

    const { data: { activeCorrectiveActionRequestAssignees } = {} } =
        useCarActiveAssigneesQuery()
    const isQCManager = useSelector(isUserQCManager)

    const shouldHideFictivAssignee = () => {
        if (
            form.getFieldValue(ASSIGNED_ENTITY) === AssignedEntityType.MP ||
            form.getFieldValue(MILESTONE) === milestone.customer_delivery
        ) {
            return true
        }
        return false
    }

    return (
        <>
            {form.getFieldValue(TYPE) !== ExceptionFailureType.IF && (
                <Form.Item
                    label={
                        <Text fontSize="16px" color="blackAlpha85">
                            Select number days late
                        </Text>
                    }
                    className={`${style.formItemMargin} ${style.daysLateStyle}`}
                >
                    {getFieldDecorator(DAYS_LATE, {
                        rules: FORM_RULES.daysLate,
                        initialValue: exception?.daysLate,
                    })(
                        <InputNumber
                            min={1}
                            placeholder="Enter days"
                            disabled={
                                !!(
                                    exception?.correctiveActionRequestId &&
                                    !isQCManager
                                )
                            }
                        />,
                    )}
                </Form.Item>
            )}
            <Text className={style.woLineTitleStyle}>
                Was the customer impacted by the failure?
            </Text>
            <Row>
                <Form.Item className={style.woLineItemsStyle}>
                    {getFieldDecorator(CUSTOMER_AFFECTED, {
                        rules: FORM_RULES.wasCustomerAffected,
                        initialValue: exception?.isCustomerImpacted || false,
                    })(
                        <Radio.Group>
                            {CUSTOMER_IMPACTED_OPTIONS.map((type, index) => (
                                <Row key={index}>
                                    <Radio
                                        data-test-target={`${type.value}-filter-checkbox`}
                                        value={type.value}
                                        disabled={
                                            !!exception?.correctiveActionRequestId &&
                                            type.value !==
                                                exception.isCustomerImpacted &&
                                            !isQCManager
                                        }
                                    >
                                        {type.label}
                                    </Radio>
                                </Row>
                            ))}
                        </Radio.Group>,
                    )}
                </Form.Item>
            </Row>
            {!shouldHideFictivAssignee() && (
                <Form.Item
                    label={
                        <Text fontSize="16px" color="blackAlpha85">
                            Fictiv assignee
                        </Text>
                    }
                    className={style.formItemMargin}
                >
                    {getFieldDecorator(FICTIV_ASSIGNEE, {
                        rules: FORM_RULES.fictivAssignee,
                        initialValue: exception?.assigneeId,
                    })(
                        <Select
                            showSearch
                            className={style.selectLayoutStyle}
                            placeholder="Select assignee"
                            filterOption={(input, option) =>
                                (option?.props?.children ?? '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            disabled={!!exception?.correctiveActionRequestId}
                        >
                            {activeCorrectiveActionRequestAssignees?.map(
                                (option, index) => (
                                    <Option value={option.id} key={index}>
                                        {option.displayName}
                                    </Option>
                                ),
                            )}
                        </Select>,
                    )}
                </Form.Item>
            )}
            <Box backgroundColor="grey3" direction="col" marginBottom="8px">
                <Box direction="col" margin="10px">
                    <Text
                        fontSize="20px"
                        fontWeight="bolder"
                        style={{ marginBottom: '10px' }}
                    >
                        Failure details
                    </Text>
                    <ExceptionFileUpload
                        exception={exception}
                        form={form}
                        confirmFileUpload={confirmFileUpload}
                        setExceptionAttachment={setExceptionAttachment}
                        exceptionAttachment={exceptionAttachment}
                    />
                </Box>
            </Box>
        </>
    )
}

export default ExceptionFailureDetailsSection
